import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Button, Stack } from '@mui/material';
import type { FieldValues, UseFormHandleSubmit } from 'react-hook-form';
import { useScrollToTheTop } from '@shared/entity';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

type FormDialogProps<TFieldValues extends FieldValues = FieldValues> = {
  id?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  title: string;
  dialogHelperText?: string;
  onOpenButton?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  handleSubmit?: UseFormHandleSubmit<TFieldValues>;
};

export default function FormDialog<TFieldValues extends FieldValues = FieldValues>(
  props: FormDialogProps<TFieldValues>,
) {
  const {
    id,
    actions,
    children,
    title,
    dialogHelperText,
    onOpenButton,
    isOpen,
    onClose,
    handleSubmit,
  } = props;
  const { t } = useIntlTranslations();
  const scroll = useScrollToTheTop();

  const onValidHandle = () => {};
  const onInvalidHandle = () => {};

  return (
    <>
      {onOpenButton}
      <Dialog
        id={id}
        open={isOpen}
        onClose={() => {
          onClose();
          scroll();
        }}
        slotProps={{
          paper: {
            component: 'form',
            onSubmit: (e: React.SubmitEvent<HTMLFormElement>) => {
              e.preventDefault();
              handleSubmit?.(onValidHandle, onInvalidHandle)();
              scroll();
            },
            sx: { width: { md: '40vw', xs: '100vw' } },
          },
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {dialogHelperText && <DialogContentText>{dialogHelperText}</DialogContentText>}
          <Stack direction="column" rowGap={2} paddingY={2}>
            {children}
          </Stack>
        </DialogContent>
        <DialogActions>
          {actions}
          <Stack direction="row" columnGap={2}>
            <Button onClick={onClose}>{t('formDialog.close')}</Button>
            <Button type="submit">{t('formDialog.save')}</Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
